import { ActionReference, DefaultGameState, GameObjectReference, GameState } from "@shared/types";
import { css, html, htmlArray } from "../helpers/webComponents";
import { GameEventService } from "../services/GameEventService";
import { GameRouteService } from "../services/GameRouteService";
import { Page } from "../enums/Page";
import { InventoryComponent } from "./InventoryComponent";
import { RootComponent } from "./RootComponent";
import { MapComponent } from "./MapComponent";

/** CSS affecting the {@link CanvasComponent} */
const styles: string = css`  
/* 
// main cointainer gedeelte
*/  
:host {
    display: flex;
    flex-direction: column;
    height: 90vh;
    width: 100%;
    margin-top: 5%;
}
*/     
.wrapper_whole {
    font-family: "Onesize";
    display: grid;
    grid-template-columns: 25% 50% 20%;
    justify-content: space-between;
    padding: 30px;

}

.wrap_gm {
    display: flex;
    flex-direction: column;
    max-height: 91vh;
    border-radius: 12px;
    padding: 10px;
    background: rgba(41, 35, 35, 0.85);
    border: 1px solid rgba(255, 255, 255, 0.2);
    box-sizing: border-box;
}
/* 
// header gedeelte (room naam en room foto)
*/
.title {
    text-align: center;
    margin-bottom: 10px;
}

.header {
    display: flex;
    flex-direction: column;
    align-items: center;
    position: relative;
    max-height: 50%;
}

.header img {
    width: auto;
    max-width: 100%;
    height: 100%;
    image-rendering: pixelated;
    object-fit: contain;
    border-radius: 10px;

}

.header img:nth-child(n + 2) {
    position: absolute;
}
/* 
// Midden gedeelte (tekst)
*/
.content {
    flex-grow: 1;
    overflow: auto;
    margin-top: 10px;
    padding: 0 10px;
}

.content p {
    margin: 0 0 10px 0;
}

.content p:last-of-type {
    margin: 0;
}
/* 
// footer gedeelte (knoppen)
*/
.footer {
    margin-top: 10px;
    border-radius: 10px;
    background-color: #292929;
    border: 1px solid #f77f56;
    display: flex;
    box-shadow: rgba(0, 0, 0, 0.35) 0px 5px 15px;
    height: auto;
    padding: 10px;
}

.footer .buttons {
    display: flex;
    flex-direction: column;
    overflow: auto;
}

.footer .button {
    border-radius: 4px;
    background-color: #f77f56;
    border: 1px solid #332c57;
    padding: 5px 10px;
    margin: 0 0 10px 10px;
    text-transform: uppercase;
    cursor: pointer;
    font-size: 19px;
    display: inline-block;
    user-select: none;
    position: relative;
    overflow: hidden;
    transition: background-color 0.3s ease-in-out, transform 0.3s ease-in-out;
}

.choice_buttons .button{
    background-color: #806ed700;
    border: 2px solid #f77f56 !important;
}

.choice_buttons .button.active,
.choice_buttons .button:hover {
    background-color: #f77f56 !important;
}

.choice_buttons .button::before {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: rgba(255, 255, 255, 0.2);
    transition: left 0.3s ease-in-out;
}

.choice_buttons .button:hover::before {
    left: 0;
}

.footer .button.active,
.footer .button:hover {
    background-color: #ff7243;
}

.footer .button::before {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: rgba(255, 255, 255, 0.2);
    transition: left 0.3s ease-in-out;
}

.footer .button:hover::before {
    left: 0;
}
/* css voor de scrollbar: */
.content::-webkit-scrollbar {
    width: 8px;
}

.content::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 10px;
}

.content::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.3);
    border-radius: 10px;
}

.content::-webkit-scrollbar-thumb:hover {
    background: rgba(255, 255, 255, 0.5);
}
.disabled_start{
    pointer-events: none;
    opacity: 0.1;
}
.start-warning {
    font-size: 0.9rem;
    color: #000000;
    margin-top: 5px;
    display: none;
}

.start-warning.visible {
    display: block;
}

`;

/**
 * Represents the Canvas page
 */
export class CanvasComponent extends HTMLElement {
    /** Instance of the game event service */
    private readonly _gameEventService: GameEventService = new GameEventService();
    /** Instance of the game route service */
    private readonly _gameRouteService: GameRouteService = new GameRouteService();

    /** Current game state */
    private _currentGameState?: DefaultGameState;
    /** Current active action button */
    private _selectedActionButton?: ActionReference;
    /** Current active game object buttons */
    private _selectedGameObjectButtons: Set<GameObjectReference> = new Set<GameObjectReference>();

    /**
     * The "constructor" of a Web Component
     */
    public connectedCallback(): void {
        this.attachShadow({ mode: "open" });

        void this.refreshGameState();
    }

    /**
     * Refresh the current game state
     */
    private async refreshGameState(): Promise<void> {
        const state: GameState = await this._gameRouteService.getGameState();

        this.updateGameState(state);
    }

    /**
     * Update the canvas to the provided game state
     *
     * @param state Game state to update the canvas to
     */
    private updateGameState(state: GameState): void {
        // Handle switching pages, if requested.
        if (state.type === "switch-page") {
            this._gameEventService.switchPage(state.page as Page);

            return;
        }

        // Reset the component
        this._currentGameState = state;

        this._selectedActionButton = undefined;
        this._selectedGameObjectButtons.clear();

        // Refresh the web component
        this.render();
    }

    /**
     * Render the contents of this page
     */
    private render(): void {
        if (!this.shadowRoot) {
            return;
        }

        const elements: HTMLElement[] = htmlArray`
            <style>
                ${styles}
            </style>

            ${this.renderTitle()}
            ${this.renderHeader()}
         <sound-settings></sound-settings>
            ${this.renderFooter()}
            ${this.renderContent()}
        `;

        while (this.shadowRoot.firstChild) {
            this.shadowRoot.firstChild.remove();
        }

        this.shadowRoot.append(...elements);
        this.renderText();
    }

    /**
     * Render the title element
     *
     * @returns String with raw HTML for the title element. Can be empty.
     */
    private renderTitle(): string {
        const roomName: string | undefined = this._currentGameState?.roomName;

        if (roomName) {
            return `<div class="title">${roomName}</div>`;
        }

        return "";
    }

    /**
     * Render the header element
     *
     * @returns String with raw HTML for the header element. Can be empty.
     */
    private renderHeader(): string {
        const roomImages: string[] | undefined = this._currentGameState?.roomImages;

        if (roomImages && roomImages.length > 0) {
            return `
                <div class="header">
                    ${roomImages.map(url => `<img src="/assets/img/rooms/${url}.png" />`).join("")}
                </div>
            `;
        }

        return "";
    }

    /**
     * Render the content element
     *
     * @returns String with raw HTML for the content element
     */
    private renderContent(): string {
        return `
            <div class="content"></div>
        `;
    }

    private currentIntervals: (NodeJS.Timeout | null)[] = [];

    private typewriterEffect(text: string, container: HTMLElement, speed: number, paragraphIndex: number, callback: () => void): void {
        let index: number = 0;
        const paragraphElement: HTMLElement = container.querySelectorAll("p")[paragraphIndex] as HTMLElement;

        if (this.currentIntervals[paragraphIndex]) {
            clearInterval(this.currentIntervals[paragraphIndex]);
        }

        this.currentIntervals[paragraphIndex] = setInterval(() => {
            if (index < text.length) {
                paragraphElement.innerHTML = text.substring(0, index + 1);
                index++;
            }
            else {
                if (this.currentIntervals[paragraphIndex]) {
                    clearInterval(this.currentIntervals[paragraphIndex]);
                }
                callback();
            }
        }, speed);
    }

    public renderText(): void {
        const container: HTMLElement = this.shadowRoot!.querySelector(".content")!;
        container.innerHTML = "";

        const textArray: string[] = (this._currentGameState?.text || []).filter(text => text.trim() !== "");

        const startTypingEffect: (index?: number) => void = (index: number = 0): void => {
            if (index < textArray.length) {
                const pElement: HTMLParagraphElement = document.createElement("p");
                container.appendChild(pElement);
                this.typewriterEffect(textArray[index], container, 25, index, () => startTypingEffect(index + 1));
            }
        };

        startTypingEffect();
    }

    /**
     * Render the footer element
     *
     * @returns HTML element of the footer
     */
    private renderFooter(): HTMLElement {
        let validObjects: GameObjectReference[] = [];
        if (this._selectedActionButton) {
            // Filter the objects so that only the valid objects are showen
            validObjects = this._currentGameState?.objects.filter(gameObject =>
                this._selectedActionButton?.validObjects?.includes(gameObject.alias)
            ) || [];
        }
        if (validObjects.length === 0) {
            validObjects = this._currentGameState?.objects || [];
        }

        return html`
            <div class="footer">
                <div class="buttons">
                    <div>
                        ${this._currentGameState?.actions.map(button => this.renderActionButton(button))}
                    </div>
                    <div class="choice_buttons">
                        ${this._selectedActionButton
                            ? validObjects.map(button => this.renderGameObjectButton(button))
                            : ""
                        }
                    </div>
                </div>
            </div>
        `;
    }

    /**
     * Render an action button for a given action reference
     *
     * @returns HTML element of the action button
     */
    private renderActionButton(button: ActionReference): HTMLElement {
        const username: string | null = localStorage.getItem("username");
        const isStartGame: boolean = button.alias === "start-game";
        const shouldDisable: boolean = isStartGame && !username;

        const element: HTMLElement = html`
        <a 
            class="button ${this._selectedActionButton === button ? "active" : ""} ${shouldDisable ? "disabled_start" : ""}" 
            title=${shouldDisable ? "Enter your name first to start the game" : ""}
        >
            ${button.name}
            <div class="start-warning ${shouldDisable ? "visible" : ""}">
                Enter your name first to unlock this action
            </div>
        </a>
    `;

        element.addEventListener("click", () => {
            if (!shouldDisable) {
                // eslint-disable-next-line @typescript-eslint/no-floating-promises
                this.handleClickAction(button);
            }
        });

        return element;
    }

    /**
     * Render a game object button for a given game object reference
     *
     * @returns HTML element of the game object button
     */
    private renderGameObjectButton(button: GameObjectReference): HTMLElement {
        const element: HTMLElement = html`
            <a class="button ${this._selectedGameObjectButtons.has(button) ? "active" : ""}">
                ${button.name}
            </a>
        `;

        element.addEventListener("click", () => this.handleClickGameObject(button));

        return element;
    }

    /**
     * Handle the click on an action button
     *
     * @param button Action button that was clicked
     */
    private async handleClickAction(button: ActionReference): Promise<void> {
        // If this actions needs a game object, show the available game objects.
        if (button.needsObject) {
            this._selectedActionButton = button;
            this._selectedGameObjectButtons.clear();

            this.render();
            return;
        }

        // Otherwise, execute the action and update the game state.
        const state: GameState | undefined = await this._gameRouteService.executeAction(button.alias);

        if (button.alias === "start-game") {
            const RootElement: Element | null | undefined = document.querySelector("game-root");
            if (RootElement) {
                void (RootElement as RootComponent).render();
            }
        }
        const MapComponent: Element | null | undefined = document.querySelector("game-root")?.shadowRoot?.querySelector("game-map");
        if (MapComponent) {
            (MapComponent as MapComponent).connectedCallback();
        }

        if (state === undefined) {
            return;
        }

        this.updateGameState(state);
    }

    /**
     * Handle the click on a game object button
     *
     * @param button Game object button that was clicked
     */
    private async handleClickGameObject(button: GameObjectReference): Promise<void> {
        // If no action button was clicked, do not try to handle this click.
        if (!this._selectedActionButton) {
            return;
        }

        // Add the game object to list of selected game objects
        this._selectedGameObjectButtons.add(button);

        // Try to execute the action with all game objects on the list
        const state: GameState | undefined = await this._gameRouteService.executeAction(
            this._selectedActionButton.alias,
            [...this._selectedGameObjectButtons].map(e => e.alias)
        );

        // After the object has been pressed, re-render the inventory
        const InventoryElement: Element | null | undefined = document.querySelector("game-root")?.shadowRoot?.querySelector("game-inventory");
        if (InventoryElement) {
            void (InventoryElement as InventoryComponent).render();
        }

        // If 2 more game objects where on the list, clear it.
        if (this._selectedGameObjectButtons.size >= 2) {
            this._selectedActionButton = undefined;
            this._selectedGameObjectButtons.clear();
        }

        // Refresh the web component
        this.render();

        // If no state was returned, exit silently. This can happen when an action needs more than 1 game object.
        if (state === undefined) {
            return;
        }

        // Otherwise, update the game state.
        this.updateGameState(state);
    }
}
